// @ts-nocheck
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import ManageInventory from '@/components/modals/inventory/manage-inventory';
import ToastContext from '@/utils/toast-context';
import PS from '@/services/permission/permission-service';
import SCComboBox from '@/components/sc-controls/form-controls/sc-combobox';
import InventoryItemModal from "@/PageComponents/Inventory/InventoryItemModal";
import {Box, Divider, Flex, Text, Tooltip} from "@mantine/core";
import stockService from '@/services/stock/stock-service';
import { useQuery } from "@tanstack/react-query";
import { getSystemNameForFormName } from "@/PageComponents/Settings/Field Settings/fieldSettingHelper";
import { getFieldSettings } from "@/PageComponents/Settings/Field Settings/FieldSettings";
import useDebouncedCallback from "@restart/hooks/useDebouncedCallback";
import useValueChangeEffect from "@/hooks/useValueChangeEffect";
import StockItemTypeIcon from "@/PageComponents/Inventory/StockItemTypeIcon";
import helper from '@/utils/helper';
import Image from "next/image";

function StockBreakdownContent({ item, stocks, loading }: { item: any; stocks: any[] | null; loading: boolean }) {
  if (!item.IsQuantityTracked) return null;
  if (loading || !stocks) return <Text size="xs">Loading...</Text>;

  const warehouses = stocks.filter((s: any) => s.Warehouse?.WarehouseType === Enums.WarehouseType.Warehouse);
  const vans = stocks.filter((s: any) => s.Warehouse?.WarehouseType === Enums.WarehouseType.Mobile);
  const visibleSum = stocks.reduce((sum: number, s: any) => sum + (s.QuantityOnHand ?? 0), 0);
  const otherQty = (item.StockQuantity ?? 0) - visibleSum;
  const hasVisibleStock = warehouses.length > 0 || vans.length > 0;

  return (
    <Box style={{ minWidth: 220 }}>
      {warehouses.length > 0 && (
        <>
          <Text size="sm" c="dimmed" mb={8}>Warehouses</Text>
          {warehouses.map((s: any) => (
            <Flex key={s.ID} justify="space-between" gap="xl" mb={4}>
              <Text size="sm">{s.WarehouseName}</Text>
              <Text size="sm" fw="bold">{s.QuantityOnHand ?? 0}</Text>
            </Flex>
          ))}
        </>
      )}
      {vans.length > 0 && (
        <>
          {warehouses.length > 0 && <Divider my={10} />}
          <Text size="sm" c="dimmed" mb={8}>Vans</Text>
          {vans.map((s: any) => (
            <Flex key={s.ID} justify="space-between" gap="xl" mb={4}>
              <Text size="sm">{s.WarehouseName}</Text>
              <Text size="sm" fw="bold">{s.QuantityOnHand ?? 0}</Text>
            </Flex>
          ))}
        </>
      )}
      {otherQty > 0 && (
        <>
          {hasVisibleStock && <Divider my={10} />}
          <Flex justify="space-between" gap="xl">
            <Text size="sm" c="dimmed">Other</Text>
            <Text size="sm" fw="bold" c="dimmed">{otherQty}</Text>
          </Flex>
        </>
      )}
      {item.Integrated && (
        <Flex justify="space-between" gap="xl" mt={4}>
          <Text size="sm">Accounting Quantity:</Text>
          <Text size="sm" fw="bold">{item.Quantity}</Text>
        </Flex>
      )}
      <Divider my={10} />
      <Flex justify="space-between" gap="xl">
        <Text size="sm">Total</Text>
        <Text size="sm" fw="bold">{item.StockQuantity ?? 0}</Text>
      </Flex>
    </Box>
  );
}

function StockBreakdownTooltip({ item }: { item: any }) {
  const [stocks, setStocks] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const handleMouseEnter = async () => {
    if (!fetchedRef.current && item.IsQuantityTracked) {
      fetchedRef.current = true;
      setLoading(true);
      try {
        const result = await stockService.getWarehouseStockForInventory(item.ID);
        setStocks(result);
      } catch {
        setStocks([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const label = <StockBreakdownContent item={item} stocks={stocks} loading={loading} />;

  return (
    <Tooltip
      label={label}
      withinPortal
      position="bottom"
      withArrow
      multiline
      disabled={!item.IsQuantityTracked}
      events={{ hover: true, focus: false, touch: false }}
      styles={{
        tooltip: {
          backgroundColor: 'white',
          color: 'inherit',
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        arrow: { backgroundColor: 'white', border: '1px solid #dee2e6' },
      }}
    >
      <Text fw={'normal'}
        size={'sm'}
        ml={'auto'}
        style={{ cursor: 'default' }}
        onMouseEnter={handleMouseEnter}
      >
        Total Stock Qty: {item.IsQuantityTracked ? item.StockQuantity : '-'}
      </Text>
    </Tooltip>
  );
}

function InventorySelector({ selectedInventory, setSelectedInventory, setInventoryChanged = undefined, accessStatus, error = undefined, ignoreIDs = [], cypress = "",
  isRequired = true, ignoreAddOption = false, placeholder = "Select inventory", onCreateNewInventoryItem, label = "Inventory", canClear = true,
  disabled = false, readOnly = false, warehouseID = null, additionalQueryParams: queryParams = null, disableIDs = [], autoFocus = false }: any) {

  /** fieldSettings Start */
  const inventoryFieldSettings = useQuery(['inventoryFieldSettings'], () => getFieldSettings(Enums.Module.Inventory))
  const settingsBySystemName = useMemo(() => {
    if (inventoryFieldSettings.data) {
      return inventoryFieldSettings.data.reduce((previousValue: any, currentValue: any) => ({
        ...previousValue,
        [currentValue.FieldSystemName]: { ...currentValue }
      }), {})
    } else {
      return {}
    }
  }, [inventoryFieldSettings.data])

  const isShown = useCallback((name: any) => {
    const systemName = getSystemNameForFormName(name)
    return settingsBySystemName.hasOwnProperty(systemName) ? settingsBySystemName[systemName].IsActive : false
  }, [settingsBySystemName])
  /** fieldSettings End */

  const toast = useContext(ToastContext);
  const [inventoryPermission] = useState(PS.hasPermission(Enums.PermissionName.Inventory));

  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const [additionalQueryParams, setAdditionalQueryParams] = useState(queryParams)
  const handleQueryParamsUpdate = useDebouncedCallback((newQueryParams: any) => {
    setAdditionalQueryParams(newQueryParams)
    setTriggerRefresh((p: any) => !p)
  }, 100)
  useValueChangeEffect(queryParams, handleQueryParamsUpdate)

  const handleInventoryChange = (inventory: any) => {
    if (!inventory) {
      setAdditionalQueryParams({})
    }
    setSelectedInventory(inventory);
    setInventoryChanged && setInventoryChanged(true);
  };

  const [pageSize] = useState(10);

  const searchInventorySC = async (skipIndex: any, take: any, filter: any) => {
    const inventory = await Fetch.post({
      url: `/Inventory/GetInventories`,
      params: {
        pageSize: take, pageIndex: skipIndex, searchPhrase: filter,
        SortExpression: "", SortDirection: "", ExcludeIDList: ignoreIDs,
        WarehouseID: warehouseID,
        PopulateThumbnails: true,
        ...additionalQueryParams
      }
    });

    let results = inventory.Results;

    return { data: results, total: inventory.TotalResults };
  };


  const [showLegacyCreateInventory, setShowLegacyCreateInventory] = useState(false);

  const addNewInventory = () => {

    if (!!onCreateNewInventoryItem) {
      onCreateNewInventoryItem();
    } else {
      setShowLegacyCreateInventory(true)
    }
  };

  const onInventoryCreate = (inventory: any) => {
    if (inventory) {
      setTriggerRefresh(!triggerRefresh);
      setSelectedInventory(inventory);
      setInventoryChanged && setInventoryChanged(true);

      //   message: 'Inventory created successfully',
      //   show: true,
      //   type: Enums.ToastType.success
    }
    setShowLegacyCreateInventory(false);
  };

  const valueRender = (element: any, value: any) => {
    if (!value) {
      return element;
    }

    const children = [
      <span
        key={1}
        style={{
          display: "inline-block",
          background: "#333",
          color: "#fff",
          borderRadius: "50%",
          width: "18px",
          height: "18px",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        {value}
      </span>,
      <span key={2}>&nbsp; {element.props.children}</span>,
    ];
    return React.cloneElement(element, { ...element.props }, children);
  };

  // useEffect(() => {
  //   //console.warn(selectedInventory?.ThumbnailUrl)
  // }, [selectedInventory])


  return (
    <>


      <SCComboBox
        autoFocus={autoFocus}
        addOption={(inventoryPermission && !ignoreAddOption ? { text: "Add new inventory", action: () => addNewInventory() } : "")}
        dataItemKey="ID"
        textField={"Description"}
        error={error}
        extraClasses="inventory-selector"
        cypress={cypress ? cypress : null}
        getOptions={searchInventorySC}
        pageSize={pageSize}
        label={label}
        name="Inventory"
        onChange={handleInventoryChange}
        value={selectedInventory}
        required={isRequired}
        triggerRefresh={triggerRefresh}
        placeholder={placeholder}
        itemRenderMantine={(itemProps: any) => {
          return (<Flex
            direction={'column'}
            gap={5}
            w={'100%'}
            py={1}
          >
            <Flex align={'center'} justify={'left'} gap={7} w={'100%'}>

              <Box pos="relative" w={40} h={40}>
                {(itemProps.dataItem.ThumbnailUrl || itemProps.dataItem.ImageUrl) ? (
                    <Image
                        src={itemProps.dataItem.ThumbnailUrl || itemProps.dataItem.ImageUrl || ''}
                        alt=""
                        width={36}
                        height={36}
                        style={{
                          borderRadius: '4px',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                    />
                ) : (
                    <Box pos={'absolute'} top={'50%'} left={'50%'} style={{transform: 'translate(-50%, -50%)'}}>
                      <StockItemTypeIcon stockItemType={itemProps.dataItem.StockItemType} size={25} />
                    </Box>
                )}
              </Box>


              <Flex direction={'column'} gap={0} align={'left'} w={'100%'} >
                <Flex
                    w={'100%'}
                    direction={'row'}
                    gap={0}
                    align={'center'}
                    justify={'space-between'}
                    wrap={'wrap-reverse'}
                >
                  <Text fw={'bold'} size={'sm'}>{itemProps.dataItem.Description}</Text>
                  <Text fw={'bolder'} size={'sm'} ml={'auto'} mr={0} >{itemProps.dataItem.Code}</Text>   
                  </Flex>
                    <Flex w={'100%'} align={'flex-end'}
                    direction={'column-reverse'}>
                        <Text size={'sm'} c={'#777E82'}>Total Stock Qty: {itemProps.dataItem.IsQuantityTracked ? itemProps.dataItem.StockQuantity : '-'}</Text>
                    </Flex>
                  <Flex
                    direction={'row'}
                    gap={'sm'}
                    align={'center'}
                    justify={'space-between'}
                >
                  {
                    // (isShown('InventoryCategory') || isShown('InventorySubcategory')) &&
                    <Flex
                        gap={5}
                        align={'center'}
                        wrap={'wrap'}
                    >
                      {(itemProps.dataItem.ThumbnailUrl || itemProps.dataItem.ImageUrl) && <StockItemTypeIcon stockItemType={itemProps.dataItem.StockItemType}/>}
                      {isShown('InventorySubcategory') && itemProps.dataItem.InventorySubcategoryDescription && <Text fw={'normal'} size={'xs'}>{itemProps.dataItem.InventorySubcategoryDescription}</Text>}
                      {isShown('InventorySubcategory') && itemProps.dataItem.InventorySubcategoryDescription && <Text fw={'lighter'} size={'xs'}> - </Text>}
                      {isShown('InventoryCategory') && itemProps.dataItem.InventoryCategoryDescription && <Text fw={'lighter'} size={'xs'}>{itemProps.dataItem.InventoryCategoryDescription}</Text>}
                    </Flex>
                  }
                  {
              /*      itemProps.dataItem.Integrated && (helper.isInventoryWarehoused(itemProps.dataItem) || itemProps.dataItem.Quantity !== 0) &&
                      <Text fw={'normal'} size={'sm'}>Acc Qty: {itemProps.dataItem.Quantity}</Text>
              */    }
                </Flex>
              </Flex>
            </Flex>


          </Flex>);
        }}
        filterFunction={(text: any, item: any) => {
          // could filter by code and/or description, but this is doing server side filtering already and don't want to affect the results coming back in case the filter changes
          return true;
        }}
        forceBlurOnChange
        canClear={canClear}
        hideDataItemKeys={ignoreIDs}
        disabled={disabled}
        readOnly={readOnly}
        cascadeDependency={warehouseID}
        disableIDs={disableIDs}
      />

      {
        showLegacyCreateInventory && <InventoryItemModal isNew={true} onInventorySave={onInventoryCreate} accessStatus={accessStatus} onClose={() => setShowLegacyCreateInventory(false)} /> ||
        showLegacyCreateInventory && <ManageInventory isNew={true} onInventorySave={onInventoryCreate} accessStatus={accessStatus} />
      }
    </>
  )
}

export default InventorySelector;
